import { lazy, FC, Suspense } from "react";
import { Route, Routes, Navigate } from "react-router-dom";
import { MasterLayout } from "../../_mcp/layout/MasterLayout";
import { WithChildren } from "../../_mcp/helpers";
import { getCSSVariableValue } from "../../_mcp/assets/ts/_utils";
import TopBarProgress from "react-topbar-progress-indicator";
import { useAuth } from "../modules/auth";
import UserManagementPage from "../pages/user-management/UserManagementPage";
import HomePageWrapper from "../pages/homepage/HomePageWrapper";

const MerchantRoutes = () => {
  const { currentUser } = useAuth();

  // Determine user role and redirect accordingly
  const getUserRole = () => {
    if (!currentUser?.auth?.role) return null;
    return currentUser.auth.role;
  };

  const role = getUserRole();

  // Check if user has MERCHANT role, redirect to auth if not
  if (!role?.startsWith("MERCHANT")) {
    return <Navigate to="/auth" replace />;
  }

  const TransfersPage = lazy(() => import("../pages/transfer/TransfersPage"));
  const UserFundsPage = lazy(() => import("../pages/user-funds/UserFundsPage"));
  const RefundsPage = lazy(() => import("../pages/refunds/RefundsPage"));
  const CheckUserFundsPage = lazy(
    () => import("../pages/check-user-funds/UserFundsPage")
  );
  const TransactionHistoryPage = lazy(
    () => import("../pages/transaction-history/TransactionHistoryPage")
  );

  // Role-based route access
  const canAccessUserManagement = role === "MERCHANT_ADMIN";
  const canAccessTransfers =
    role === "MERCHANT_MAKER" || role === "MERCHANT_APPROVAL";
  const canAccessUserFunds = role === "MERCHANT_MAKER";
  const canAccessRefunds = role === "NONE";
  const canAccessTransactionHistory =
    role === "MERCHANT_ADMIN" ||
    role === "MERCHANT_MAKER" ||
    role === "MERCHANT_APPROVAL";

  return (
    <Routes>
      <Route element={<MasterLayout />}>
        {/* Merchant Pages */}
        <Route
          path="auth/*"
          element={<Navigate to="/merchant/transaction-history" />}
        />
        <Route
          path="homepage/*"
          element={
            <SuspensedView>
              <HomePageWrapper />
            </SuspensedView>
          }
        />
        <Route
          path="user-management/*"
          element={
            canAccessUserManagement ? (
              <SuspensedView>
                <UserManagementPage />
              </SuspensedView>
            ) : (
              <Navigate to="/merchant/transaction-history" replace />
            )
          }
        />
        <Route
          path="transfers/*"
          element={
            canAccessTransfers ? (
              <SuspensedView>
                <TransfersPage />
              </SuspensedView>
            ) : (
              <Navigate to="/merchant/transaction-history" replace />
            )
          }
        />
        <Route
          path="user-funds/*"
          element={
            canAccessUserFunds ? (
              <SuspensedView>
                <UserFundsPage />
              </SuspensedView>
            ) : (
              <Navigate to="/merchant/transaction-history" replace />
            )
          }
        />
        <Route
          path="refunds/*"
          element={
            canAccessRefunds ? (
              <SuspensedView>
                <RefundsPage />
              </SuspensedView>
            ) : (
              <Navigate to="/merchant/transaction-history" replace />
            )
          }
        />
        <Route
          path="check-user-funds/*"
          element={
            <SuspensedView>
              <CheckUserFundsPage />
            </SuspensedView>
          }
        />
        <Route
          path="transaction-history/*"
          element={
            canAccessTransactionHistory ? (
              <SuspensedView>
                <TransactionHistoryPage />
              </SuspensedView>
            ) : (
              <Navigate to="/merchant/transaction-history" replace />
            )
          }
        />
        {/* Page Not Found */}
        <Route path="*" element={<Navigate to="/error/404" />} />
      </Route>
    </Routes>
  );
};

const SuspensedView: FC<WithChildren> = ({ children }) => {
  const baseColor = getCSSVariableValue("--kt-primary");
  TopBarProgress.config({
    barColors: {
      "0": baseColor,
    },
    barThickness: 1,
    shadowBlur: 5,
  });
  return <Suspense fallback={<TopBarProgress />}>{children}</Suspense>;
};

export { MerchantRoutes };
